import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface OrderItem {
  articleId: string
  name: string
  quantity: number
  price: number
  subtotal: number
}

export interface Order {
  id: string
  restaurantId: string
  orderNumber: string
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'completed' | 'cancelled'
  items: OrderItem[]
  subtotal: number
  tax: number
  total: number
  customerName: string
  customerPhone?: string
  orderType: 'dine-in' | 'takeout' | 'delivery'
  createdAt: string
  updatedAt: string
}

export const useOrdersStore = defineStore('orders', () => {
  const orders = ref<Order[]>([
    {
      id: '1',
      restaurantId: '1',
      orderNumber: 'ORD-001',
      status: 'confirmed',
      items: [
        {
          articleId: '1',
          name: 'Margherita Pizza',
          quantity: 2,
          price: 12.99,
          subtotal: 25.98,
        },
        {
          articleId: '3',
          name: 'Tiramisu',
          quantity: 1,
          price: 7.99,
          subtotal: 7.99,
        },
      ],
      subtotal: 33.97,
      tax: 3.40,
      total: 37.37,
      customerName: 'John Doe',
      customerPhone: '+1 555 123 4567',
      orderType: 'dine-in',
      createdAt: '2024-03-15T10:30:00Z',
      updatedAt: '2024-03-15T10:35:00Z',
    },
    {
      id: '2',
      restaurantId: '1',
      orderNumber: 'ORD-002',
      status: 'preparing',
      items: [
        {
          articleId: '2',
          name: 'Pasta Carbonara',
          quantity: 1,
          price: 14.99,
          subtotal: 14.99,
        },
      ],
      subtotal: 14.99,
      tax: 1.50,
      total: 16.49,
      customerName: 'Jane Smith',
      customerPhone: '+1 555 234 5678',
      orderType: 'takeout',
      createdAt: '2024-03-15T11:00:00Z',
      updatedAt: '2024-03-15T11:10:00Z',
    },
  ])

  const addOrder = (order: Omit<Order, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newOrder: Order = {
      ...order,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    orders.value.push(newOrder)
    return newOrder
  }

  const updateOrder = (id: string, updates: Partial<Order>) => {
    const index = orders.value.findIndex((o) => o.id === id)
    if (index !== -1) {
      orders.value[index] = {
        ...orders.value[index],
        ...updates,
        updatedAt: new Date().toISOString(),
      }
    }
  }

  const deleteOrder = (id: string) => {
    orders.value = orders.value.filter((o) => o.id !== id)
  }

  const getOrder = (id: string) => {
    return orders.value.find((o) => o.id === id)
  }

  const getRestaurantOrders = (restaurantId: string) => {
    return orders.value.filter((o) => o.restaurantId === restaurantId)
  }

  const getRestaurantOrdersByStatus = (restaurantId: string, status: Order['status']) => {
    return orders.value.filter((o) => o.restaurantId === restaurantId && o.status === status)
  }

  const reset = () => {
    orders.value = []
  }

  return {
    orders,
    addOrder,
    updateOrder,
    deleteOrder,
    getOrder,
    getRestaurantOrders,
    getRestaurantOrdersByStatus,
    reset,
  }
})
